import React, { useEffect, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import {
  format,
  parse,
  startOfWeek,
  getDay,
  isSameDay,
  isWithinInterval,
} from "date-fns";
import "react-big-calendar/lib/css/react-big-calendar.css";
import styles from "./eventCalender.module.css";
import {
  Modal,
  Form,
  Input,
  Button,
  List,
  DatePicker,
  Select,
  Spin,
} from "antd";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  createEvent,
  deleteEvent,
  getEvent,
  updateEvent,
} from "../../../../services/event";
import { getAllLevels } from "../../../../services/announcement";
import CustomButton from "../../../../common/custom-button/custom-button";

const locales = { "en-US": require("date-fns/locale/en-US") };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

const { RangePicker } = DatePicker;
const { Option } = Select;

const EventCalendar = () => {
  const [events, setEvents] = useState([]);
  const [dayEvents, setDayEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [viewDate, setViewDate] = useState(new Date());

  const [addForm] = Form.useForm();
  const [levels, setLevels] = useState([]);

  const fetchEvents = async (month, year) => {
    try {
      setLoading(true);
      const res = await getEvent(month, year);
      const fetched = res.data.data.map((ev) => ({
        id: ev.id,
        title: ev.event_name,
        start: new Date(ev.start_date),
        end: new Date(ev.end_date),
        recipient: ev.recipient,
        level_ids: ev.level_ids,
      }));
      setEvents(fetched);
      return fetched;
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch events");
      return [];
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const now = new Date();
    fetchEvents(now.getMonth() + 1, now.getFullYear());
    fetchLevels();
  }, []);

  const fetchLevels = async () => {
    try {
      const response = await getAllLevels();
      setLevels(response.data.data.levels || []);
    } catch (err) {
      console.error(err);
    }
  };

  const openDayPopup = (date) => {
    const filtered = events.filter(
      (e) =>
        isSameDay(e.start, date) ||
        isWithinInterval(date, { start: e.start, end: e.end })
    );

    setDayEvents(filtered);
    setSelectedDate(date);
    setIsModalOpen(true);
  };

  const handleDrillDown = (date) => {
    openDayPopup(date);
    return false;
  };

  const openAddModal = (eventToEdit = null, date = null) => {
    setEditingEvent(eventToEdit);

    if (eventToEdit) {
      addForm.setFieldsValue({
        title: eventToEdit.title,
        range: [dayjs(eventToEdit.start), dayjs(eventToEdit.end)],
        recipient: eventToEdit.recipient || "student",
        levels: eventToEdit.level_ids || [],
      });
    } else if (date) {
      addForm.resetFields();
      const day = dayjs(date);
      addForm.setFieldsValue({
        range: [day, day],
      });
    } else {
      addForm.resetFields();
    }

    setIsAddModalOpen(true);
  };

  const fetchDailyEvents = (allEvents) => {
    if (selectedDate) {
      const filtered = allEvents.filter(
        (e) =>
          isSameDay(e.start, selectedDate) ||
          isWithinInterval(selectedDate, { start: e.start, end: e.end })
      );
      setDayEvents(filtered);
    }
  };

  const handleGlobalAdd = async () => {
    try {
      setSaving(true);
      const values = await addForm.validateFields();
      const [start, end] = values.range;

      const formData = new FormData();
      formData.append("event_name", values.title);
      formData.append("recipient", values.recipient);
      formData.append("start_date", start.format("YYYY-MM-DD"));
      formData.append("end_date", end.format("YYYY-MM-DD"));
      formData.append("level_ids", JSON.stringify(values.levels));
      if (editingEvent) {
        await updateEvent(formData, editingEvent.id);
        toast.success("Event updated");
      } else {
        await createEvent(formData);
        toast.success("Event added");
      }

      addForm.resetFields();
      setIsAddModalOpen(false);
      setEditingEvent(null);

      const latestEvents = await fetchEvents(
        viewDate.getMonth() + 1,
        viewDate.getFullYear()
      );

      fetchDailyEvents(latestEvents);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (eventToDelete) => {
    if (!eventToDelete?.id) return;

    try {
      setDeleting(true);
      await deleteEvent(eventToDelete.id);
      toast.success("Event deleted");

      const latestEvents = await fetchEvents(
        viewDate.getMonth() + 1,
        viewDate.getFullYear()
      );

      fetchDailyEvents(latestEvents);

      setIsAddModalOpen(false);
      setIsModalOpen(false);
      setEditingEvent(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete event");
    } finally {
      setDeleting(false);
    }
  };

  const handleSelectAll = (value) => {
    if (value.includes("all")) {
      if (value.length === levels.length + 1) {
        return [];
      }
      return levels.map((item) => item.id);
    }
    return value;
  };

  const CustomToolbar = ({ label, onNavigate, date }) => {
    const today = new Date();
    const isCurrentMonth =
      today.getMonth() === date.getMonth() &&
      today.getFullYear() === date.getFullYear();

    return (
      <div>
        <div className={styles.addEventWrapper}>
          <Button 
            className={`${styles.navBtn} ${styles.todayBtn}`} 
            onClick={() => onNavigate("TODAY")}
            disabled={isCurrentMonth}
          >
            Go to Today
          </Button>
          <Button type="primary" onClick={() => openAddModal()}>
            + Add Event
          </Button>
        </div>
        <div className={styles.toolbar}>
          <div className={styles.monthLabelWrapper}>
            <button className={styles.navBtn} onClick={() => onNavigate("PREV")}>‹</button>
            <span className={styles.monthLabel}>{label}</span>
            <button className={styles.navBtn} onClick={() => onNavigate("NEXT")}>›</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>

      <Spin spinning={loading}>
        <div className={styles.calendarWrapper}>
          <Calendar
            localizer={localizer}
            events={events}
            components={{
              toolbar: CustomToolbar,
            }}
            startAccessor="start"
            endAccessor="end"
            style={{ height: "100%" }}
            selectable
            onSelectEvent={(events) => openAddModal(events)}
            onSelectSlot={({ start }) => openAddModal(null, start)}
            onDrillDown={handleDrillDown}
            views={["month"]}
            onNavigate={(date) => {
              setViewDate(date);
              const month = date.getMonth() + 1;
              const year = date.getFullYear();
              fetchEvents(month, year);
            }}
          />
        </div>
      </Spin>

      <Modal
        title={`Events on ${selectedDate ? format(selectedDate, "PPP") : ""}`}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={600}
        bodyStyle={{ maxHeight: "50vh", overflowY: "auto" }}
      >
        {dayEvents.length === 0 ? (
          <>
            <p>No events yet. Add one below:</p>
            <div className={styles.addBtnBox}>
              <CustomButton
                type="Submit"
                variant="outlined"
                onClick={() => openAddModal(null, selectedDate)}
                loading={loading}
                title="Add Event"
              />
            </div>
          </>
        ) : (
          <>
            <div className={styles.addBtnBox}>
              <CustomButton
                type="Submit"
                variant="outlined"
                onClick={() => openAddModal(null, selectedDate)}
                loading={loading}
                title="Add Event"
              />
            </div>
            <List
              dataSource={dayEvents}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Button type="link" onClick={() => openAddModal(item)}>
                      Edit
                    </Button>,
                    <Button
                      danger
                      type="link"
                      onClick={() => handleDelete(item)}
                    >
                      Delete
                    </Button>,
                  ]}
                >
                  <div className={styles.eventItem}>
                    <strong>{item.title}</strong>
                    <span className={styles.eventDate}>
                      {format(item.start, "yyyy-MM-dd")} —{" "}
                      {format(item.end, "yyyy-MM-dd")}
                    </span>
                  </div>
                </List.Item>
              )}
            />
          </>
        )}
      </Modal>

      <Modal
        title={editingEvent ? "Update Event" : "Add New Event"}
        open={isAddModalOpen}
        onCancel={() => setIsAddModalOpen(false)}
        zIndex={2000}
        destroyOnClose
        footer={null}
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            label="Event Title"
            name="title"
            rules={[{ required: true, message: "Enter event title!" }]}
          >
            <Input placeholder="Event title" />
          </Form.Item>

          <Form.Item
            label="Date Range"
            name="range"
            rules={[{ required: true, message: "Select start & end date" }]}
          >
            <RangePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label="Recipient"
            name="recipient"
            rules={[{ required: true, message: "Select recipient" }]}
          >
            <Select mode="multiple" placeholder="Select Recipient" allowClear>
              <Option value="student">Student</Option>
              <Option value="parent">Parent</Option>
              <Option value="teacher">Teacher</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Select Levels"
            name="levels"
            rules={[{ required: true, message: "Please select levels" }]}
          >
            <Select
              mode="multiple"
              placeholder="Select levels"
              allowClear
              style={{ width: "100%" }}
              onChange={(value) =>
                addForm.setFieldsValue({ levels: handleSelectAll(value) })
              }
            >
              <Option key="all" value="all">
                ---SELECT ALL---
              </Option>
              {levels.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <div className={styles.btn}>
              {editingEvent && (
                <CustomButton
                  type="Submit"
                  variant="outlined"
                  onClick={() => handleDelete(editingEvent)}
                  loading={deleting}
                  style={{ background: "#ff4d4f" }}
                  title="Delete Event"
                />
              )}
              <CustomButton
                type="Submit"
                variant="outlined"
                onClick={handleGlobalAdd}
                loading={saving}
                title={editingEvent ? "Update Event" : "Add Event"}
              />
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EventCalendar;
